import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { TCSLogo } from '../components/TCSLogo';
import { Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Read values directly from the form — works with Safari autofill
    // which sometimes does not fire React onChange events.
    const form = e.currentTarget;
    const emailValue = (form.elements.email?.value || email || '').trim();
    const passwordValue = form.elements.password?.value || password || '';

    try {
      await login(emailValue, passwordValue);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        setError(formatApiErrorDetail(detail));
      } else if (err.message === 'Network Error') {
        setError('Sem conexão com o servidor. Verifique sua internet e tente novamente.');
      } else {
        setError('Email ou senha inválidos. Verifique os dados e tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/d0c3862c-91cf-4abe-86c7-d1852ab015aa/images/2165cc64cd0fa469bc14e5db51b7bfd3536e050111542ec559eaebfb3a9231b4.png)'
        }}
      >
        <div className="absolute inset-0 bg-black/80"></div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-black/60 backdrop-blur-xl border border-white/10 p-8">
            <div className="flex flex-col items-center mb-8">
              <TCSLogo size="lg" showTagline={true} />
            </div>

            <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6 text-center" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Login
            </h2>

            {error && (
              <div className="mb-4 p-3 bg-[#FF3366]/10 border border-[#FF3366]/50 text-[#FF3366] text-sm" data-testid="login-error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={(e) => setEmail(e.target.value)}
                  required
                  className="bg-[#121212] border-white/10 text-white focus:border-[#00F0FF] focus:ring-[#00F0FF] rounded-none h-12 autofill-dark"
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">
                  Senha
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    autoComplete="current-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={(e) => setPassword(e.target.value)}
                    required
                    className="bg-[#121212] border-white/10 text-white focus:border-[#00F0FF] focus:ring-[#00F0FF] rounded-none h-12 pr-12 autofill-dark"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#00F0FF] transition-colors"
                    data-testid="login-toggle-password"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#00F0FF] hover:bg-[#4DFFFF] text-black font-bold uppercase tracking-wide h-12 rounded-none transition-all"
                data-testid="login-submit-button"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-3">
              <Link to="/forgot-password" className="text-[#00F0FF] hover:text-[#4DFFFF] text-sm font-semibold block" data-testid="login-forgot-link">
                Esqueci minha senha
              </Link>
              <p className="text-[#A1A1AA] text-sm">
                Não tem uma conta?{' '}
                <Link to="/register" className="text-[#00F0FF] hover:text-[#4DFFFF] font-bold" data-testid="login-register-link">
                  Cadastre-se
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
